import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { CountryCode } from 'libphonenumber-js';
import type { PhoneNumber } from '@klareospace/pharos-lib-types';
import {
  countryCodeToFlag,
  getAllCountryOptions,
  resolveDialCode,
  toNationalNumber,
} from '@klareospace/pharos-lib-front-utils/utils';
import type { CountryOption } from '@klareospace/pharos-lib-front-utils/utils';
import { getExampleNationalNumber } from '@klareospace/pharos-lib-front-utils/phone-validation';
import { useSelectedCountry } from '@klareospace/pharos-lib-front-utils';

import type { PopoverSide } from '../../../lib/popover-side';
import { computePopoverSide } from '../../../lib/popover-side';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

export interface PhoneInputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'type' | 'value'> {
  /** Accessible label for the dial code combobox trigger. Defaults to an English fallback. */
  countryAriaLabel?: string;
  /** Text shown when no country matches the search query. Defaults to an English fallback. */
  emptyText?: string;
  /** Locale for country display names and search (e.g. `'fr'`, `'ar'`). Defaults to `'en'`. */
  locale?: string;
  onChange?: (value: PhoneNumber | null) => void;
  /** Placeholder for the country search box. Defaults to an English fallback. */
  searchPlaceholder?: string;
  value?: PhoneNumber | null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface PhoneCountryPickerProps {
  countryAriaLabel: string;
  disabled?: boolean;
  emptyText: string;
  onSelect: (code: CountryCode) => void;
  options: CountryOption[];
  searchPlaceholder: string;
  selectedCountry: CountryCode;
}

/**
 * Searchable dial code picker — flag + dial code in the trigger, flag + name + dial code in the
 * list. The vertical side is computed once at open time from the trigger's position (toward
 * whichever side has more viewport room) and pinned for the whole open session, so the popover
 * doesn't flip between top and bottom as the filtered list's height changes.
 */
function PhoneCountryPicker({
  countryAriaLabel,
  disabled,
  emptyText,
  onSelect,
  options,
  searchPlaceholder,
  selectedCountry,
}: PhoneCountryPickerProps): React.JSX.Element {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [side, setSide] = React.useState<PopoverSide>('bottom');
  const dialCode = resolveDialCode(selectedCountry);

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen) {
      setSide(computePopoverSide(triggerRef.current));
    }
    setIsOpen(nextOpen);
  }

  function handleSelect(code: CountryCode): void {
    onSelect(code);
    setIsOpen(false);
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label={countryAriaLabel}
          className="h-full w-auto shrink-0 gap-1 rounded-none rounded-s-md border-0 border-e border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
          disabled={disabled}
          ref={triggerRef}
          role="combobox"
          type="button"
          variant="ghost"
        >
          {countryCodeToFlag(selectedCountry)} {dialCode}
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" side={side}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <PhoneCountryItem
                  isSelected={option.code === selectedCountry}
                  key={option.code}
                  onSelect={handleSelect}
                  option={option}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface PhoneCountryItemProps {
  isSelected: boolean;
  onSelect: (code: CountryCode) => void;
  option: CountryOption;
}

function PhoneCountryItem({ isSelected, onSelect, option }: PhoneCountryItemProps): React.JSX.Element {
  return (
    <CommandItem
      keywords={[option.dialCode, option.code]}
      onSelect={(): void => onSelect(option.code)}
      value={option.displayName}
    >
      <Check className={cn('h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
      <span className="me-1">{option.flag}</span>
      <span className="flex-1 truncate">{option.displayName}</span>
      <span className="text-muted-foreground">{option.dialCode}</span>
    </CommandItem>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * A controlled phone input component that displays a searchable dial code combobox
 * and an editable national number input.
 *
 * - Accepts and emits `PhoneNumber` objects (`{ dialCode, number }`)
 * - The selected country is local state seeded from `value.dialCode` — no `defaultCountry` prop
 *   needed, and selecting any country sharing a calling code (e.g. Canada under +1) sticks
 *   instead of snapping back to that code's "main" country on the next render
 * - User types only the national number portion
 * - Emits `null` when the national number is cleared
 * - Dial code picker lists every country known to libphonenumber-js, filterable by country
 *   name or dial code (flag + dial code collapsed, flag + name + dial code expanded); country
 *   names and sort order follow the `locale` prop (defaults to `'en'`) — pass the app's active
 *   language (e.g. `i18n.language`) so French/Arabic users can search in their own language
 * - Placeholder defaults to a country-appropriate example number and updates when the dial
 *   code changes; an explicit `placeholder` prop always wins
 * - `countryAriaLabel`/`searchPlaceholder`/`emptyText` default to English fallbacks — callers
 *   should pass translated strings (e.g. from the `common` i18n namespace) alongside `locale`
 * - Supports RTL layout via CSS logical properties
 * - Forwards `ref` to the inner `<input>` so FormControl (Radix Slot) can associate
 *   `id`, `aria-describedby`, `aria-invalid`, and `ref` with the actual input element
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      countryAriaLabel = 'Phone country code',
      disabled,
      emptyText = 'No country found.',
      locale = 'en',
      onChange,
      placeholder,
      searchPlaceholder = 'Search country or dial code...',
      value,
      ...inputProps
    },
    ref,
  ) => {
    const [selectedCountry, setSelectedCountry] = useSelectedCountry(value?.dialCode);
    const dialCode = resolveDialCode(selectedCountry);
    const nationalNumber = value?.number ?? '';
    const resolvedPlaceholder = placeholder ?? getExampleNationalNumber(selectedCountry);
    const countryOptions = getAllCountryOptions(locale);

    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
      const digits = event.target.value;

      if (!digits) {
        onChange?.(null);
        return;
      }

      const number = toNationalNumber(digits, selectedCountry);
      onChange?.({ dialCode, number });
    }

    function handleCountrySelect(code: CountryCode): void {
      setSelectedCountry(code);
      const newDialCode = resolveDialCode(code);
      onChange?.({ dialCode: newDialCode, number: value?.number ?? '' });
    }

    return (
      <div
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background text-base ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 md:text-sm',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <PhoneCountryPicker
          countryAriaLabel={countryAriaLabel}
          disabled={disabled}
          emptyText={emptyText}
          onSelect={handleCountrySelect}
          options={countryOptions}
          searchPlaceholder={searchPlaceholder}
          selectedCountry={selectedCountry}
        />

        {/* National number input — ref and inputProps forwarded here for FormControl/Slot compatibility */}
        <input
          {...inputProps}
          ref={ref}
          className="flex-1 bg-transparent px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed"
          disabled={disabled}
          onChange={handleChange}
          placeholder={resolvedPlaceholder}
          type="tel"
          value={nationalNumber}
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';
